/**
 * BudgetCard - Summary card for a budget in the list
 */

import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { BudgetSummary } from '@budgira/shared';
import { cn } from '../../utils/cn';

interface BudgetCardProps {
  budget: BudgetSummary;
  onDelete: (id: string) => void;
  onDuplicate: (id: string) => void;
  /** Whether the user can manage (edit/delete/duplicate) this budget. Defaults to true for backwards compatibility. */
  canManage?: boolean;
}

export const BudgetCard = ({ budget, onDelete, onDuplicate, canManage = true }: BudgetCardProps) => {
  const { t, i18n } = useTranslation();

  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString(i18n.language === 'fr' ? 'fr-FR' : 'en-US', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
    });
  };

  const formatAmount = (amount: number) => {
    return amount.toLocaleString(i18n.language === 'fr' ? 'fr-FR' : 'en-US', {
      style: 'currency',
      currency: 'EUR',
    });
  };

  const isPositive = budget.netResult >= 0;
  const isDraft = budget.status === 'draft';

  return (
    <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 p-4 hover:shadow-md transition-shadow">
      <div className="flex items-start justify-between mb-3">
        <div className="flex-1 min-w-0">
          <Link
            to={`/budgets/${budget.id}`}
            className="text-lg font-semibold text-slate-900 dark:text-slate-100 hover:text-violet-600 dark:hover:text-violet-400 truncate block"
          >
            {budget.name}
          </Link>
          <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">
            {formatDate(budget.startDate)} - {formatDate(budget.endDate)}
          </p>
        </div>
        <div className="flex items-center gap-1">
          {/* Status badge */}
          <span
            className={cn(
              'px-2 py-1 text-xs font-medium rounded-full',
              isDraft
                ? 'bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300'
                : 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300'
            )}
          >
            {isDraft ? t('budgets.draft') : t('budgets.validated')}
          </span>
        </div>
      </div>

      {/* Amounts */}
      <div className="space-y-2 mb-4">
        <div className="flex justify-between items-center text-sm">
          <span className="text-slate-500 dark:text-slate-400">{t('budgets.income')}</span>
          <span className="font-medium text-green-600 dark:text-green-400">
            {formatAmount(budget.totalIncome)}
          </span>
        </div>
        <div className="flex justify-between items-center text-sm">
          <span className="text-slate-500 dark:text-slate-400">{t('budgets.expenses')}</span>
          <span className="font-medium text-red-600 dark:text-red-400">
            {formatAmount(budget.totalExpenses)}
          </span>
        </div>
        <div className="border-t border-slate-200 dark:border-slate-700 pt-2">
          <div className="flex justify-between items-center">
            <span className="text-sm font-medium text-slate-700 dark:text-slate-300">
              {t('budgets.netResult')}
            </span>
            <span
              className={cn(
                'text-lg font-bold',
                isPositive
                  ? 'text-green-600 dark:text-green-400'
                  : 'text-red-600 dark:text-red-400'
              )}
            >
              {isPositive ? '+' : ''}
              {formatAmount(budget.netResult)}
            </span>
          </div>
        </div>
      </div>

      {/* Footer */}
      <div className="flex items-center justify-between pt-3 border-t border-slate-100 dark:border-slate-700">
        <div className="flex items-center gap-2 text-xs text-slate-400 dark:text-slate-500">
          <span>v{budget.version}</span>
          <span>•</span>
          <span>
            {budget.accountCount} {budget.accountCount > 1 ? 'comptes' : 'compte'}
          </span>
        </div>
        <div className="flex items-center gap-1">
          {/* Duplicate button - only if user can manage */}
          {canManage && (
            <button
              onClick={(e) => {
                e.preventDefault();
                onDuplicate(budget.id);
              }}
              className="p-2 text-slate-400 hover:text-violet-600 dark:hover:text-violet-400 transition-colors"
              title={t('budgets.duplicate')}
            >
              <CopyIcon className="w-4 h-4" />
            </button>
          )}
          <Link
            to={`/budgets/${budget.id}`}
            className="p-2 text-slate-400 hover:text-violet-600 dark:hover:text-violet-400 transition-colors"
            title={t('budgets.viewDetails')}
          >
            <EyeIcon className="w-4 h-4" />
          </Link>
          {/* Edit button - only for draft budgets AND if user can manage */}
          {isDraft && canManage && (
            <Link
              to={`/budgets/${budget.id}?edit=true`}
              className="p-2 text-slate-400 hover:text-violet-600 dark:hover:text-violet-400 transition-colors"
              title={t('common.edit')}
            >
              <EditIcon className="w-4 h-4" />
            </Link>
          )}
          {/* Delete button - only if user can manage */}
          {canManage && (
            <button
              onClick={(e) => {
                e.preventDefault();
                onDelete(budget.id);
              }}
              className="p-2 text-slate-400 hover:text-red-600 dark:hover:text-red-400 transition-colors"
              title={t('common.delete')}
            >
              <TrashIcon className="w-4 h-4" />
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

const EyeIcon = ({ className }: { className?: string }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
    />
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
    />
  </svg>
);

const TrashIcon = ({ className }: { className?: string }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
    />
  </svg>
);

const CopyIcon = ({ className }: { className?: string }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"
    />
  </svg>
);

const EditIcon = ({ className }: { className?: string }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
    />
  </svg>
);

export default BudgetCard;
